// ── 대시보드 유틸 함수 및 상수 ────────────────────────────────────────────────
import { toDateOnlyUtc, diffMinutes as _diffMinutes, parseTimestamp } from '../../utils/date';

// 모바일 판단 기준 너비 (px)
export const MOBILE_BREAKPOINT = 768;

// ─── KST 날짜/시간 포맷터 ────────────────────────────────────────────────────
/** YYYY-MM-DD 형식 포맷터 (KST) */
export const KST_DATE_FORMATTER = new Intl.DateTimeFormat('sv-SE', { timeZone: 'Asia/Seoul' });
/** 요일 단축 포맷터 (월, 화, ... KST) */
export const KST_WEEKDAY_FORMATTER = new Intl.DateTimeFormat('ko-KR', {
  timeZone: 'Asia/Seoul', weekday: 'short',
});
/** HH:MM 시간 포맷터 (KST, 24시간) */
export const KST_TIME_FORMATTER = new Intl.DateTimeFormat('ko-KR', {
  timeZone: 'Asia/Seoul', hour12: false, hour: '2-digit', minute: '2-digit',
});

// ─── 헬퍼 함수 ───────────────────────────────────────────────────────────────
/** IPv4-mapped IPv6 주소 정규화 (::ffff: 접두어 제거) */
export function normalizeIp(ip) {
  if (!ip) return '-';
  return String(ip).replace(/^::ffff:/, '').trim();
}

/** 날짜 문자열 → Date 객체 변환 (유효하지 않으면 null) */
export function toDateSafe(value) {
  if (!value) return null;
  const text = String(value).trim();
  // toDateOnlyUtc: utils/date.js 사용 (new Date 직접 사용 금지)
  // YYYY-MM-DD 형식: UTC 자정 Date 생성 (Intl 포맷터가 timeZone으로 보정)
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return toDateOnlyUtc(text);
  // TIMESTAMPTZ/ISO 문자열: toDateOnlyUtc로 파싱 (시각 정보 불필요 — Intl formatter 사용)
  return toDateOnlyUtc(value);
}

/** 날짜 포맷 (YYYY-MM-DD, KST) */
export function fmtDate(value) {
  const dt = toDateSafe(value);
  if (!dt) return '-';
  return KST_DATE_FORMATTER.format(dt);
}

/** 요일 첫 글자 반환 (월, 화, ... KST) */
export function weekdayOne(value) {
  const dt = toDateSafe(value);
  if (!dt) return '-';
  return KST_WEEKDAY_FORMATTER.format(dt).slice(0, 1);
}

/** 시간 포맷 (HH:MM, KST) — TIMESTAMPTZ 시각 정보 보존 */
export function fmtTime(value) {
  // parseTimestamp: 시간 정보를 보존하는 Date 변환 (toDateOnlyUtc는 시간을 자정으로 잘라냄)
  const dt = parseTimestamp(value);
  if (!dt) return '-';
  return KST_TIME_FORMATTER.format(dt);
}

/** 두 시각 사이 분 차이 계산 (start, end는 ISO 문자열 또는 Date) */
export function diffMinutes(start, end) {
  if (!start || !end) return null;
  // diffMinutes: utils/date.js 사용 (new Date 직접 사용 금지)
  const diff = _diffMinutes(start, end);
  if (!Number.isFinite(diff) || diff <= 0) return null;
  return diff;
}

/**
 * 근로시간 기반 자동 휴게시간 정책 (분)
 * - 6시간 30분 이상: 120분
 * - 4시간 이상: 30분
 * - 그 미만: 0분
 */
export function breakMinutesByPolicy(totalMinutes) {
  if (!Number.isFinite(totalMinutes)) return null;
  if (totalMinutes >= 390) return 120;
  if (totalMinutes >= 4 * 60) return 30;
  return 0;
}

/** 분 → "N시간 M분" 문자열 변환 */
export function formatMinutes(minutes) {
  if (!Number.isFinite(minutes) || minutes < 0) return '-';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${h}시간 ${m}분`;
}

/**
 * 출퇴근 행 요약 (출근/퇴근/근무시간/휴게시간) 계산
 * @param {object} row - 출퇴근 기록 행 (check_in, check_out, break_minutes 등)
 * @returns {{ checkIn, checkOut, worked, rest }} 포맷된 출퇴근 요약 정보
 */
export function getAttendanceSummary(row) {
  const total = diffMinutes(row?.check_in, row?.check_out);
  const autoRest = breakMinutesByPolicy(total);
  // null/undefined는 fallback 대상이므로 Number()로 변환하지 않음 (Number(null)=0 → ?? 체인 차단 방지)
  const effectiveRest = (row?.effective_break_minutes != null && Number.isFinite(Number(row.effective_break_minutes)))
    ? Number(row.effective_break_minutes) : null;
  const manualRest = (row?.break_minutes != null && Number.isFinite(Number(row.break_minutes)))
    ? Number(row.break_minutes) : null;
  // 우선순위: 수동입력 > effective_break > 자동정책
  const rest = manualRest ?? effectiveRest ?? autoRest;
  const serverWorked = Number(row?.worked_minutes);
  const worked = Number.isFinite(serverWorked)
    ? serverWorked
    : (Number.isFinite(total) && Number.isFinite(rest) ? Math.max(0, total - rest) : null);
  return {
    checkIn: fmtTime(row?.check_in),
    checkOut: row?.check_out ? fmtTime(row?.check_out) : '미퇴근',
    worked: formatMinutes(worked),
    rest: formatMinutes(rest),
  };
}

/**
 * 연차 행이 특정 필터 키에 해당하는지 판단
 * @param {object} row - 연차 기록 행
 * @param {string} filterKey - 필터 키 (generated|used|paid_recognized|unpaid|settled|family|bonus|corrected)
 */
export function matchesLeaveFilter(row, filterKey) {
  const type = String(row?.type || '').trim();
  const category = String(row?.category || '').trim();
  const balanceEffect = String(row?.leave_balance_effect || 'legacy');
  const payrollEffect = String(row?.payroll_effect || 'legacy');
  if (filterKey === 'generated') return balanceEffect === 'grant' || (balanceEffect === 'legacy' && (type === '생성' || type === '자동생성'));
  if (filterKey === 'used') return balanceEffect === 'deduct' || (balanceEffect === 'legacy' && (type === '연차' || type === '반차' || type === '반차(전)' || type === '반차(후)') && category === '유급');
  if (filterKey === 'paid_recognized') return payrollEffect === 'paid_cover' && balanceEffect === 'none';
  if (filterKey === 'unpaid') return payrollEffect === 'unpaid_absence' || (payrollEffect === 'legacy' && category === '무급');
  if (filterKey === 'settled') return balanceEffect === 'settlement' || (balanceEffect === 'legacy' && (type === '정산' || category === '정산'));
  if (filterKey === 'family') return type === '경조사' || category === '경조사';
  if (filterKey === 'bonus') return type === '포상' || category === '포상';
  if (filterKey === 'corrected') return Boolean(row?.is_correction) || type === '정정' || category === '정정';
  return true;
}
